
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { API_URL } from "../config";
import { getMessage } from "../data/locales";
import PasswordRequirements from "../components/PasswordRequirements";
import { isStrongPassword } from "../utils/passwordPolicy";
import "./SetupAccount.css";

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const pendingOrderId = searchParams.get("orderId");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [timerId, setTimerId] = useState(null);

  useEffect(() => {
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [timerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");

    if (password !== confirmPassword) {
      setError(getMessage("setupAccount.mismatch"));
      return;
    }

    if (!isStrongPassword(password)) {
      setError(getMessage("passwordPolicy.invalid"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/usuarios/setup-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, confirmPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        setMsg(getMessage("setupAccount.success"));
        const id = setTimeout(() => {
          navigate(
            pendingOrderId
              ? `/checkout?orderId=${encodeURIComponent(pendingOrderId)}`
              : "/login",
          );
        }, 2000);
        setTimerId(id);
      } else {
        setError(data.error || getMessage("setupAccount.genericError"));
      }
    } catch {
      setError(getMessage("setupAccount.connectionError"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="setup-page">
        <div className="setup-card">
          <p className="setup-error">{getMessage("setupAccount.invalidToken")}</p>
          <Link to="/login" className="setup-back-link">{getMessage("setupAccount.backToLogin")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-page">
      <div className="setup-wrapper">
        <div className="setup-header">
          <img
            src="/logos/LOGO_ACADEMIA.png"
            alt={getMessage("setupAccount.logoAlt")}
            className="setup-logo"
            draggable="false"
          />
        </div>
        <div className="setup-card">
          <h2 className="setup-title">{getMessage("setupAccount.title")}</h2>
          <p className="setup-subtitle">{getMessage("setupAccount.subtitle")}</p>

          {msg ? <div className="setup-success-msg" role="status">{msg}</div> : null}
          {error ? <div className="setup-error-msg" role="alert">{error}</div> : null}

          <form onSubmit={handleSubmit} className="setup-form">
            <div className="setup-form-group">
              <label htmlFor="setup-password">{getMessage("setupAccount.newPassword")}</label>
              <div className="setup-password-field">
                <input
                  id="setup-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="setup-password-toggle"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={
                    showPassword
                      ? getMessage("resetPassword.hidePassword")
                      : getMessage("resetPassword.showPassword")
                  }
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>

            <PasswordRequirements />

            <div className="setup-form-group">
              <label htmlFor="setup-confirm-password">{getMessage("setupAccount.confirmPassword")}</label>
              <div className="setup-password-field">
                <input
                  id="setup-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="setup-password-toggle"
                  onClick={() => setShowConfirmPassword((visible) => !visible)}
                  aria-label={
                    showConfirmPassword
                      ? getMessage("resetPassword.hideConfirm")
                      : getMessage("resetPassword.showConfirm")
                  }
                  aria-pressed={showConfirmPassword}
                >
                  {showConfirmPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="setup-submit-btn">
              {loading ? getMessage("setupAccount.saving") : getMessage("setupAccount.save")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetupAccount;
